import { supabase } from '../lib/supabase';

const API_BASE_URL = process.env.REACT_APP_SUPABASE_URL;

/**
 * Dashboard Service
 * Handles all dashboard-related API calls
 */
export const dashboardService = {
  /**
   * Fetch dashboard summary data
   * @returns {Promise<Object>} Dashboard summary data
   */
  async getSummary() {
    try {
      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('認証が必要です');
      }

      // Call the Edge Function
      const response = await fetch(`${API_BASE_URL}/functions/v1/dashboard-summary`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Dashboard API error: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Dashboard summary error:', error);
      throw error;
    }
  },


  /**
   * Update course progress
   * @param {string} courseId - Course ID
   * @param {number} completedLessons - Number of completed lessons
   * @returns {Promise<Object>} Updated progress data
   */
  async updateCourseProgress(courseId, completedLessons) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('認証が必要です');
      }

      const { data, error } = await supabase
        .from('course_progress')
        .upsert({
          user_id: user.id,
          course_id: courseId,
          completed_lessons: completedLessons,
          last_access_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,course_id'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update course progress error:', error);
      throw error;
    }
  },

  /**
   * Record a learning session
   * @param {Object} sessionData - Session data
   * @returns {Promise<Object>} Created session data
   */
  async recordLearningSession(sessionData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('認証が必要です');
      }

      const { data, error } = await supabase
        .from('learning_sessions')
        .insert({
          user_id: user.id,
          ...sessionData
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Record learning session error:', error);
      throw error;
    }
  },

  /**
   * Update user goal progress
   * @param {string} goalId - Goal ID
   * @param {number} currentValue - Current progress value
   * @returns {Promise<Object>} Updated goal data
   */
  async updateGoalProgress(goalId, currentValue) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('認証が必要です');
      }

      const { data, error } = await supabase
        .from('user_goals')
        .update({ current_value: currentValue })
        .eq('id', goalId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update goal progress error:', error);
      throw error;
    }
  },

  /**
   * Get learning time statistics for charts
   * @param {string} periodType - Period type (daily, weekly, monthly)
   * @param {number} limit - Number of periods to fetch
   * @returns {Promise<Array>} Time statistics
   */
  async getLearningTimeStats(periodType = 'daily', limit = 30) {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('User not authenticated');
        return [];
      }

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - limit);

      // Try to fetch from learning_time_stats view/table first
      const { data: stats, error: statsError } = await supabase
        .from('learning_time_stats')
        .select('*')
        .eq('user_id', user.id)
        .eq('period_type', periodType)
        .gte('period_date', startDate.toISOString().split('T')[0])
        .lte('period_date', endDate.toISOString().split('T')[0])
        .order('period_date', { ascending: true });

      if (statsError) {
        console.error('Error fetching learning time stats:', statsError);
        // Fall back to aggregating from sessions
        return await this.aggregateLearningTimeFromSessions(user.id, periodType, limit);
      }

      // Fill in missing dates with zero values
      const filledStats = [];
      const statsMap = new Map(stats.map(s => [s.period_date, s]));
      
      for (let i = 0; i < limit; i++) {
        const date = new Date(endDate);
        date.setDate(date.getDate() - (limit - 1 - i));
        const dateStr = date.toISOString().split('T')[0];
        
        if (statsMap.has(dateStr)) {
          filledStats.push(statsMap.get(dateStr));
        } else {
          filledStats.push({
            period_date: dateStr,
            period_type: periodType,
            total_minutes: 0,
            session_count: 0
          });
        }
      }

      return filledStats;
    } catch (error) {
      console.error('Error getting learning time stats:', error);
      return [];
    }
  },

  /**
   * Fallback method to aggregate learning time directly from sessions
   */
  async aggregateLearningTimeFromSessions(userId, periodType = 'daily', limit = 30) {
    try {
      const { data: sessions, error } = await supabase
        .from('learning_sessions')
        .select('started_at, duration_min')
        .eq('user_id', userId)
        .order('started_at', { ascending: false });

      if (error) {
        console.error('Error fetching sessions for aggregation:', error);
        return [];
      }

      console.log('Aggregating from sessions:', sessions);

      // 簡単な日次集計
      const dailyStats = {};
      sessions.forEach(session => {
        const date = new Date(session.started_at).toISOString().split('T')[0];
        if (!dailyStats[date]) {
          dailyStats[date] = { total_minutes: 0, session_count: 0 };
        }
        dailyStats[date].total_minutes += session.duration_min || 0;
        dailyStats[date].session_count += 1;
      });

      // 結果を配列に変換
      const result = Object.entries(dailyStats)
        .map(([date, stats]) => ({
          period_date: date,
          period_type: 'daily',
          total_minutes: stats.total_minutes,
          session_count: stats.session_count
        }))
        .sort((a, b) => a.period_date.localeCompare(b.period_date))
        .slice(-limit);

      console.log('Aggregated stats:', result);
      return result;
    } catch (error) {
      console.error('Error in fallback aggregation:', error);
      return [];
    }
  },

};